<template>
  <div>
    <vonap-window
      width="1300px"
      height="550px"
      title="FFEC Reconciliation Report DrillDown"
      :visible="true"
      @close="close"
    >
      <page-toolbar>
        <kendo-button icon="excel" title="Export to excel" @click="exportToExcel"></kendo-button>
      </page-toolbar>
      <kendo-grid
        :dataSource="dataSource"
        :editable="false"
        style="height: 470px;"
        ref="grid"
        :filterable="false"
        :sortable="false"
        :pageable-always-visible="false"
        :columns="columns"
      ></kendo-grid>
    </vonap-window>
  </div>
</template>

<script>
import VonapWindow from '@/components/VonapWindow'
import PageToolbar from '@/components/PageToolbar'
import {mapGetters} from 'vuex'

export default {
  data() {
    return {
      dataSource: new kendo.data.DataSource({
        type: 'aspnetmvc-ajax',
        serverPaging: false,
        serverFiltering: false,
        serverSorting: false,
        pageSize: 1500,
        transport: {
          read: {
            type: 'GET',
            url: '/api/heroux/ffecReconciliationReport/readDrillDown',
            data: () => ({
              clientId: this.clientId,
              fsYear: this.fsYear,
              quarter: this.quarter,
              divisions: this.divisionsList,
              ccyPairId: this.ccyPairId,
              rowNumber: this.rowNumber,
              ffecDrillDownColumn: this.ffecDrillDownColumn,
            }),
            dataType: 'json',
          },
        },
        schema: {
          errors: 'errors',
          total: 'total',
          data: 'data',
          model: {
            fields: {
              dealCode: {type: 'number'},
              division: {type: 'string'},
              counterparty: {type: 'string'},
              reference: {type: 'string'},
              tradeDate: {type: 'date'},
              maturityDate: {type: 'date'},
              settlementDate: {type: 'date'},
              ccy1: {type: 'string'},
              hedgeRate: {type: 'string'},
              buySell: {type: 'string'},
              amountCcy1: {type: 'number'},
              amountCcy1Cad: {type: 'number'},
              evaluationDate: {type: 'date'},
              riskEval: {type: 'number'},
              riskEvalCad: {type: 'number'},
            },
          },
        },
        aggregate: [
          {field: 'amountCcy1', aggregate: 'sum'},
          {field: 'amountCcy1Cad', aggregate: 'sum'},
          {field: 'riskEval', aggregate: 'sum'},
          {field: 'riskEvalCad', aggregate: 'sum'},
        ],
      }),
    }
  },
  computed: {
    ...mapGetters({tokenString: 'authentication/tokenString'}),
    ...mapGetters({clientId: 'authentication/selectedClientId'}),
    columns() {
      return [
        {field: 'dealCode', title: 'Trade Id', width: '70px'},
        {field: 'division', title: 'Division', width: '80px'},
        {field: 'counterparty', title: 'Counterparty', width: '100px'},
        {field: 'reference', title: 'Reference', width: '200px'},
        {field: 'tradeDate', title: 'Trade Date', format: '{0:yyyy-MM-dd}', width: '100px'},
        {field: 'maturityDate', title: 'Maturity Date', format: '{0:yyyy-MM-dd}', width: '100px'},
        {
          field: 'settlementDate',
          title: 'Settlement Date',
          format: '{0:yyyy-MM-dd}',
          width: '100px',
        },
        {field: 'ccy1', title: 'Ccy1', width: '60px'},
        {
          field: 'hedgeRate',
          title: 'Hedge Rate',
          format: '{0:N4}',
          width: '100px',
          footerTemplate: e => {
            const data = this.dataSource.data()
            let quantityRate = 0
            data.forEach(function(dataItem) {
              quantityRate = quantityRate + dataItem.amountCcy1 * dataItem.hedgeRate
            })

            let total = quantityRate / e.amountCcy1.sum
            return "<div style='text-align: right'>" + kendo.toString(total, 'n4') + '</div>'
          },
          attributes: {style: 'text-align: right;'},
        },
        {field: 'buySell', title: 'Buy/Sell', width: '75px'},
        {
          field: 'amountCcy1',
          title: 'Amount Ccy #1',
          format: '{0:N0}',
          width: '120px',
          attributes: {style: 'text-align: right;'},
          aggregates: ['sum'],
          footerTemplate:
            "<div style='text-align: right'>#=kendo.toString(data.amountCcy1.sum, 'n0') # </div>",
        },
        {
          field: 'amountCcy1Cad',
          title: 'Amount Ccy #1 CAD',
          format: '{0:N0}',
          width: '120px',
          attributes: {style: 'text-align: right;'},
          aggregates: ['sum'],
          footerTemplate:
            "<div style='text-align: right'>#=kendo.toString(data.amountCcy1Cad.sum, 'n0') # </div>",
        },
        {
          field: 'evaluationDate',
          title: 'Evaluation Date',
          format: '{0:yyyy-MM-dd}',
          hidden: this.ffecDrillDownColumn == 1,
          width: '100px',
        },
        {
          field: 'riskEval',
          title: 'Rsk Eval',
          format: '{0:N0}',
          width: '120px',
          attributes: {style: 'text-align: right;'},
          aggregates: ['sum'],
          hidden: this.ffecDrillDownColumn == 1,
          footerTemplate:
            "<div style='text-align: right'>#=kendo.toString(data.riskEval.sum, 'n0') # </div>",
        },
        {
          field: 'riskEvalCad',
          title: 'Rsk Eval CAD',
          format: '{0:N0}',
          width: '120px',
          attributes: {style: 'text-align: right;'},
          aggregates: ['sum'],
          hidden: this.ffecDrillDownColumn == 1,
          footerTemplate:
            "<div style='text-align: right'>#=kendo.toString(data.riskEvalCad.sum, 'n0') # </div>",
        },
      ]
    },
  },
  props: {
    fsYear: {type: Number, default: -1, required: true},
    quarter: {type: Number, default: -1, required: true},
    divisionsList: {type: Array, default: () => [], required: true},
    divisions: {type: String, default: '', required: true},
    ccyPairId: {type: Number, default: -1, required: true},
    rowNumber: {type: Number, default: -1, required: true},
    ffecDrillDownColumn: {type: Number, default: -1, required: true},
    modalVisible: Boolean,
  },
  methods: {
    exportToExcel() {
      window.location.href = `/api/heroux/FfecReconciliationReport/exportDrillDownToExcel?clientId=${
        this.clientId
      }&fsYear=${this.fsYear}&quarter=${this.quarter}&divisions=${this.divisionsList}&ccyPairId=${
        this.ccyPairId
      }&rowNumber=${this.rowNumber}&ffecDrillDownColumn=${this.ffecDrillDownColumn}&t=${
        this.tokenString
      }`
    },
    close() {
      this.$emit('close')
    },
  },
  components: {
    VonapWindow,
    PageToolbar,
  },
}
</script>
